<template>
  <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
  <provide-state-triggered-by-slot-props
    v-slot="{ isOn: expensesVisible, toggle: toggleExpenses }"
    :default="!hasExpenses"
  >
    <div>
      <div class="row text-left m-t-20">
        <div v-tooltip:top="report.description" class="col-xs-3 pointer" @click="toggleExpenses">
          <small>
            <i
              :class="{
                'fa-caret-down': expensesVisible,
                'fa-caret-right': !expensesVisible,
              }"
              class="fa m-r-5 c-gray"
            />
          </small>
          {{ report.title }}
        </div>
        <div class="col-xs-2">
          {{ report.hire?.currency || report.hire?.yjob?.currency }} {{ total }}
        </div>
        <div v-tooltip:top="report.hire?.yjob?.name" class="col-xs-1">
          <a
            v-if="report.hire_id"
            :href="`/hires/${report.hire_id}/overview`"
            class="tw-font-medium"
          >
            {{ `#${report.hire?.yjob?.localId}` }}
          </a>
        </div>
        <div class="col-xs-2" :data-test="'bill-id-' + `${report.bill_id}`">
          <a
            v-if="report.bill_id"
            :href="laravelNamedRoute(namedRoutes.bills.edit, { bill: report.bill_id })"
            class="tw-font-medium tw-grow tw-line-clamp-1 tw-break-all"
            :title="`#${report.bill_id}`"
          >
            {{ `#${report.bill_id}` }}
          </a>
        </div>
        <div class="col-xs-2">{{ formatDate(report.createdAt) }}</div>
        <div class="text-center">
          <button
            :class="{ faded: expensesVisible }"
            class="btn btn-xs btn-default tw-mr-2"
            @click="toggleExpenses"
          >
            <WdsIcon name="list" :size="17" />
          </button>
          <slot name="open-btn" />
          <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
          <provide-state-triggered-by-slot-props
            v-if="canEdit"
            v-slot="{ isOn: deleteMode, turnOn: triggerDeleteMode, turnOff: cancelDeleteMode }"
          >
            <div class="inline">
              <button
                v-tooltip:top="$t('Delete entire expense report and associated expenses')"
                class="btn btn-xs btn-default m-l-5"
                data-test="expense-report-delete-button"
                @click="triggerDeleteMode"
              >
                <i class="fa fa-trash" />
              </button>
              <ModalConfirmSwal
                v-if="deleteMode"
                :header-text="$t('Are you sure?')"
                :text="
                  $t(
                    'Do you really want to delete the entire expense report and associated expenses?',
                  )
                "
                :confirm-button-text="$t('Yes, delete')"
                :processing-text="$t('Deleting')"
                @confirmed="deleteReport"
                @cancelled="cancelDeleteMode"
              />
            </div>
          </provide-state-triggered-by-slot-props>
          <template v-if="isIssued && !isPaid">
            <span class="c-orange">
              <small>{{ $t('Awaiting payment') }}</small>
            </span>
          </template>
        </div>
      </div>
      <template v-if="expensesVisible">
        <div
          :class="{
            'border-left': hasExpenses,
            'p-t-10': hasExpenses && canEdit,
          }"
        >
          <div v-for="expense in report.expenses" :key="expense.id" class="row m-t-15">
            <div class="col-xs-12">
              <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
              <provide-state-triggered-by-slot-props
                v-slot="{ isOn: isOpen, turnOn: openEditModal, turnOff: closeEditModal }"
              >
                <div class="m-l-20">
                  <Expense
                    :editable="canEdit"
                    :expense="expense"
                    :expense-report-id="report.localId"
                    :files-required="true"
                    @trigger-edit="openEditModal"
                    @trigger-delete="openEditModal"
                  />
                  <ModalExpense
                    :view-only="!canEdit"
                    :expense-report-id="report.localId"
                    :expense-report-currency="expenseReportCurrency"
                    :is-open="isOpen"
                    :expense="expense"
                    :files-required="true"
                    :should-exclude-vat="report.hire?.hasFreelancerVat"
                    :account-id="report.hire?.company?.globalId"
                    :custom-field-provider-account-ids="customFieldProviderAccountIds"
                    @closed="closeEditModal"
                  />
                </div>
              </provide-state-triggered-by-slot-props>
            </div>
          </div>

          <div v-if="canEdit" class="row m-t-15">
            <div class="col-xs-12">
              <div
                class="btn btn-default btn-xs waves-effect pointer m-l-20"
                @click="openLineItemModal = true"
              >
                <i class="c-gray fa fa-plus-circle" />
                {{ $t('Add expense') }}
              </div>
            </div>
          </div>
        </div>
        <ModalExpense
          v-if="canEdit"
          :expense-report-id="report.localId"
          :expense-report-currency="expenseReportCurrency"
          :is-open="openLineItemModal"
          :files-required="true"
          :should-exclude-vat="report.hire?.hasFreelancerVat"
          :account-id="report.hire?.company?.globalId"
          :custom-field-provider-account-ids="customFieldProviderAccountIds"
          @closed="openLineItemModal = false"
        />
      </template>
    </div>
  </provide-state-triggered-by-slot-props>
</template>

<script>
import gql from 'graphql-tag'
import { nextTick } from 'vue'
import { WdsIcon } from '@worksome/wds-components'
import { EXPENSE_REPORT_MUTATIONS } from '@/state/mutation-types'
import ModalConfirmSwal from '@/components/ModalConfirmSwal.vue'
import Expense from '@/components/Expense.vue'
import ModalExpense from '@/components/ModalExpense.vue'
import FormatDateMixin from '@/mixins/MethodFormatDateMixin'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'

export default {
  components: { WdsIcon, Expense, ModalExpense, ModalConfirmSwal },
  mixins: [FormatDateMixin],
  props: {
    report: { type: Object, default: () => {} },
    // eslint-disable-next-line vue/no-boolean-default -- Baseline eslint ignore please fix if working on this
    editable: { type: Boolean, default: true },
    autoOpenNewExpenseModal: { type: Boolean, default: false },
  },
  data() {
    return {
      namedRoutes,
      openLineItemModal: false,
    }
  },
  computed: {
    canEdit() {
      return this.editable && !this.isIssued
    },
    total() {
      return this.hasExpenses
        ? Number(
            this.report.expenses.map((expense) => expense.amount).reduce((a, b) => a + b, 0) / 100,
          ).toFixed(2)
        : '0.00'
    },
    isIssued() {
      return this.report?.bill?.isIssued || false
    },
    isPaid() {
      return this.report.bill ? this.report.bill.isPayed || this.report.bill.isExpresspayed : false
    },
    hasExpenses() {
      return this.report.expenses.length > 0
    },
    // Expenses take the payment request's currency when the report is linked to one,
    // which can differ from the hire's.
    expenseReportCurrency() {
      return this.report.bill?.currency || this.report.hire?.currency || this.report.hire?.yjob?.currency
    },
    customFieldProviderAccountIds() {
      return (this.report.hire?.customFieldProviderCompanies ?? []).map((company) => company.globalId)
    },
  },
  created() {
    if (this.report.hasOwnProperty('autoAddExpense') && this.report.autoAddExpense) {
      nextTick(() => {
        this.openLineItemModal = true
      })
    }
  },
  methods: {
    laravelNamedRoute,
    deleteReport() {
      this.$apollo
        .mutate({
          fetchPolicy: 'no-cache',
          mutation: gql`
            mutation ($reportId: Int!) {
              deleteExpenseReport(localId: $reportId) {
                globalId
                localId
              }
            }
          `,
          variables: { reportId: this.report.localId },
        })
        .then(() => {
          this.$store.commit(EXPENSE_REPORT_MUTATIONS.DELETE_REPORT, this.report.localId)
        })
    },
  },
}
</script>

<style scoped>
.border-left {
  border-left: 1px solid lightgray;
}
</style>
